/**
 * Bilingual copy for the Nabil Tawfik site.
 * Arabic is the source of truth; English is a parallel translation.
 * Copy drawn from the author's own Arabic and English book introductions.
 */

export type Lang = "ar" | "en";

export type Dictionary = (typeof T)[Lang];

export const T = {
  ar: {
    dir: "rtl" as const,
    langLabel: "EN",
    nav: {
      home: "الرئيسية",
      book: "الكتاب",
      concepts: "الأفكار",
      videos: "لقاءات وحوارات",
      posts: "المنشورات",
      english: "الطبعة الإنجليزية",
      about: "عن المؤلف",
      legacy: "الإرث",
      contact: "اطلب الكتاب",
    },
    site: {
      name: "نبيل توفيق",
      tagline: "مفكّر وباحث في الحضارة المصرية القديمة",
    },
    hero: {
      kicker: "كتاب بقلم نبيل توفيق",
      title1: "الحضارة المصرية القديمة",
      title2: "رؤية عقلية موثّقة",
      lede:
        "دعوة إلى قراءة جديدة لأقدم حضارة عرفتها البشرية؛ قراءة تستند إلى العقل والنصّ والأثر، وتُعيد الاعتبار لإيمان المصريين القدماء بالتوحيد واتّباعهم لحضرات رسل الله على أرض مصر.",
      ctaPrimary: "اقتنِ النسخة العربية",
      ctaSecondary: "تعرّف على أفكار الكتاب",
      frameCaption: "طبعة 2015 · متوفّرة الآن",
    },
    featured: {
      kicker: "الكتاب",
      title: "عملٌ يُعيد قراءة التاريخ المصري القديم",
      body1:
        "منذ أن وطئت قدم الإنسان هذه الأرض، ظلّت الحضارة المصرية القديمة شاهدةً على عصرٍ من الإعجاز العلمي والمعماري والروحي. غير أنّ كثيرًا ممّا تناقلته كتب المستشرقين جاء مشوَّهًا، يصف حضارة التوحيد بأنها حضارة أوثان.",
      body2:
        "يأتي هذا الكتاب ليضع الأمور في نصابها، مستندًا إلى النصوص المصرية القديمة ذاتها، إلى البرديات، إلى جدران المعابد والمقابر، لِيُظهر أنّ أعظم حضارة في التاريخ إنّما كانت ثمرةَ الإيمان باللهِ الواحد واتّباعِ حضرات الرسل.",
      feature: [
        "نصوص وبرديات تُثبت الإيمان بالوحدانية",
        "رؤية جديدة لمعنى «فرعون» و«الخرطوش»",
        "أصل الصلاة والصيام والحج والزكاة في مصر القديمة",
        "صلة اللغة المصرية القديمة باللغة العربية",
      ],
      ctaBuy: "اقتنِ الكتاب",
      ctaExcerpt: "اقرأ من المقدّمة",
    },
    ideas: {
      kicker: "خلاصات من الكتاب",
      title: "أفكار تحرّك العقل",
      intro:
        "مجموعة من المحاور الرئيسية التي يطرحها الكتاب. يمكن استعراضها في صفحة «الأفكار» بقراءة متأنّية ومرتبطة بالنصّ الأصلي.",
      seeAll: "استعرض جميع الأفكار",
    },
    videos: {
      kicker: "لقاءات وحوارات",
      title: "الكلمة المسموعة",
      intro:
        "مختارات من الحوارات والمحاضرات العامة التي تحدّث فيها الأستاذ نبيل توفيق عن حضارة مصر وعقيدة أهلها.",
      seeAll: "مشاهدة المزيد",
      placeholder: "فيديو قريبًا",
    },
    legacy: {
      kicker: "الإرث",
      title: "الرسالة تستمرّ",
      body:
        "بعد رحيل الأستاذ نبيل توفيق — رحمه الله — آلَت الأمانةُ إلى من أحبّوه: أن تبقى أفكارُه متاحةً، وأن يصل كتابه إلى كل قارئٍ يبحث عن قراءةٍ عقليّة موثّقةٍ لتاريخ مصر. هذا الموقع هو البيت الرسمي لمشروعه الفكري.",
      cta: "تابع القراءة عن الإرث",
    },
    footer: {
      brand: "الموقع الرسمي للأستاذ نبيل توفيق",
      rights: "جميع الحقوق محفوظة للأستاذ نبيل توفيق",
      ipNotice:
        "هذا الموقع وما يتضمنه من نصوص ومحتوى هو ملك حصري للمؤلف، ولا يجوز نسخ أو إعادة نشر أو توزيع أي جزء منه بأي وسيلة دون الحصول على إذن مسبق.",
      nav1: "استكشف",
      nav2: "تواصل",
      email: "البريد الإلكتروني",
      social: "تابعنا",
      note: "يُحدَّث الموقع باستمرار.",
    },
    bookPage: {
      headKicker: "الطبعة العربية · الأولى",
      headTitle: "الحضارة المصرية القديمة — رؤية عقلية موثّقة",
      author: "تأليف: نبيل توفيق",
      year: "صدرت في ١ / ١ / ٢٠١٥ · دار الفكر العربي",
      overviewTitle: "عن الكتاب",
      overview: [
        "كتابٌ يكشف كثيرًا من أسرار الحضارة المصرية القديمة وارتباطها بحضرات رسل الله على أرض مصر. يستند إلى النصوص والبرديّات والنقوش، ويعتمد منهجًا عقليًا يراجع ما رسّخه المستشرقون من مفاهيم خاطئة حول وثنيّة المصريين القدماء.",
        "يُقدّم المؤلف شواهدَ متعدّدةً على إيمان المصريين القدماء بوحدانية الخالق، وعلى أدائهم لعباداتٍ تُماثل ما يؤدّيه المسلمون اليوم من صلاةٍ وصيامٍ وحجٍّ وزكاة، ويستقصي دلالات الرموز والأسماء في اللغة المصرية القديمة ويربطها بالعربية.",
      ],
      whyTitle: "لماذا يستحقّ هذا الكتاب القراءة؟",
      why: [
        { t: "قراءة منهجية", b: "يقوم على تحليل النصوص الأصلية لا على ترجمات غير أمينة." },
        { t: "شواهد متعدّدة", b: "يستند إلى برديّات وجدران معابد ومقابر ومسلّات." },
        { t: "لغة واضحة", b: "يُخاطب القارئ العام والمتخصّص معًا بأسلوبٍ متدرّج." },
        { t: "رؤية مستقلّة", b: "يتحرّر من هيمنة المدرسة الاستشراقية في تفسير التاريخ." },
      ],
      sampleTitle: "من المقدّمة",
      sample:
        "«تتميّز الحضارة المصرية القديمة بأنها الحضارة الخالدة الوحيدة التي لم تندثر آثارها عبر آلاف السنين، والتي تحكي أسرار التاريخ منذ وُجد الإنسان على هذه الأرض».",
      sampleSig: "— من مقدّمة الكتاب",
      orderTitle: "كيفيّة الحصول على الكتاب",
      order: [
        { t: "الشحن داخل مصر", b: "متاح لجميع المحافظات. يُحدّد الموعد عند الطلب." },
        { t: "الشحن الدولي", b: "متاح حسب البلد وسيُبلَّغ المشتري بالتفاصيل." },
        { t: "وسائل الدفع", b: "تحويل بنكي · محفظة إلكترونية · الدفع عند الاستلام (داخل مصر)." },
      ],
      orderCta: "اطلب نسختك الآن",
      endorseTitle: "آراء وقراءات",
      endorsePlaceholder: "مكان مخصّص لشهادات القرّاء والمفكّرين حول الكتاب — تُضاف لاحقًا.",
      flipToBack: "اعرض الغلاف الخلفي",
      flipToFront: "اعرض الغلاف الأمامي",
      librariesKicker: "في مكتبات العالم",
      librariesTitle: "الكتاب على رفوف المكتبات الأكاديميّة والوطنيّة",
      librariesIntro:
        "يوجد الكتاب ضمن مقتنيات عددٍ من المكتبات الجامعيّة والوطنيّة الكبرى حول العالم، إلى جانب توفّره لدى مكتبات إلكترونيّة عربيّة معروفة.",
      librariesViewCta: "اعرض في الفهرس",
      retailersTitle: "اقتنِ الكتاب رقمياً",
      retailersIntro:
        "للراغبين في اقتناء الكتاب، تتوفّر النسخة العربية لدى المكتبات الإلكترونيّة التالية:",
    },
    englishPage: {
      kicker: "الطبعة الإنجليزية",
      // The working English title isn't yet legally registered, so we
      // surface a placeholder here (mirrors the "TITLE / to be revealed"
      // panel on the cover image). Reinstate the real title in both
      // languages of `englishPage.title` AND in the cover image
      // (scripts/process-... regenerator) once registration completes.
      title: "العنوان قيد الإعداد",
      subtitle: "قيد الإعداد — ستصدر قريبًا بإذن الله",
      body1:
        "الترجمة الإنجليزية لكتاب «الحضارة المصرية القديمة — رؤية عقلية موثّقة» في مرحلتها النهائية من التحرير والمراجعة. أضف بريدك الإلكتروني لتكون أوّل من يُبلَّغ عند صدورها.",
      body2:
        "نحرص على أن تحافظ الترجمة على عمق النصّ الأصلي ودقّته، لذا تأخذ إجراءات التحرير وقتها اللازم.",
      formLabel: "أبلغني عند الصدور",
      formPlaceholder: "بريدك الإلكتروني",
      formCta: "سجّل اهتمامك",
      formThanks: "شكرًا — سيصلك إشعار عند الإصدار.",
    },
    conceptsPage: {
      kicker: "الأفكار",
      title: "محاور الكتاب الرئيسيّة",
      intro:
        "قائمة بأبرز الأفكار والمحاور التي يتناولها الكتاب. ستُضاف لكلٍّ منها خلاصةٌ موسَّعة ومراجع مباشرة إلى النص، بالتدريج.",
      noteLabel: "ملاحظة تحريريّة:",
      note:
        "هذه العناوين مأخوذة مباشرةً من قائمة المؤلف في مقدّمة الكتاب، وتُقدَّم هنا كمدخل للقراءة.",
      expandedTBA: "خلاصة موسَّعة — تُضاف قريبًا",
    },
    postsPage: {
      kicker: "المنشورات",
      title: "منشورات من فيسبوك",
      intro:
        "أرشيف متنامٍ لمنشورات الأستاذ نبيل توفيق على فيسبوك. تُعرض النصوص باللغة العربية كما نُشرت، مع الصور المرفقة. المصدر: النسخة الكاملة لصفحة فيسبوك، استوردت عبر برنامج محلي.",
      empty: "لا توجد منشورات حالياً.",
      loadMore: "عرض المزيد",
      viewOriginal: "شاهد المنشور الأصلي",
      postKicker: "منشور فيسبوك",
      prev: "السابق",
      next: "التالي",
      readMore: "قراءة المنشور",
      photoCount: (n: number) => `${n} صورة`,
      photoAlt: (i: number, total: number, date: string) =>
        `صورة ${i} من ${total} — ${date}`,
      backToIndex: "العودة إلى المنشورات",
    },
    videosPage: {
      kicker: "مشاهدات",
      title: "لقاءات وحوارات",
      intro:
        "أرشيف متنامٍ لمحاضرات وحوارات الأستاذ نبيل توفيق. يمكن مشاهدة القائمة الكاملة على قناة يوتيوب، وتتوفّر نصوص إنجليزية لحلقتَي «رحلة النيل».",
      watchOnYoutube: "فتح على يوتيوب",
      playlistLabel: "قائمة التشغيل الرسمية",
      transcriptToggle: "عرض النصّ المكتوب",
      transcriptHide: "إخفاء النصّ",
      transcriptNA: "لا يتوفّر نصّ مكتوب لهذه الحلقة بعد",
      arabicSummaryTBA: "ملخّص عربي — قيد الإعداد",
    },
    aboutPage: {
      kicker: "عن المؤلف",
      title: "نبيل توفيق",
      role: "مؤلّف · مفكّر · باحث في الحضارة المصرية القديمة",
      bioLead:
        "خصّص الأستاذ نبيل توفيق عقودًا من عمره لدراسة الحضارة المصرية القديمة، باحثًا في معابدها وبرديّاتها ونقوشها، بحثًا مدفوعًا بسؤالٍ جوهري: ما حقيقةُ العقيدة التي قامت عليها هذه الحضارة؟",
      // Authoritative biographical paragraphs taken verbatim from the back
      // cover of the Arabic first edition. Rendered as a distinct,
      // visually-tagged section on the About page (see About.tsx) so the
      // reader can see clearly which paragraphs are by the author and
      // which are editorial. Do not summarise or paraphrase here — these
      // are the author's own words about himself.
      bioFromBackCoverHeading: "نبذة عن المؤلف — من الغلاف الخلفي للكتاب",
      bioFromBackCover: [
        "نبيل توفيق تخرّج من كلية السياحة والفنادق جامعة حلوان عام ١٩٧٦، شعبة الإرشاد السياحي، ودرس اللغة المصرية القديمة، كما درس تاريخ وآثار مصر القديمة خلال عصورها المختلفة. وأتاحت له زياراته الميدانية لمختلف الأماكن الأثرية في أنحاء مصر خلال ستّة وثلاثين سنة من العمل الميداني في مجال الإرشاد السياحي، بالإضافة إلى العديد من القراءات والأبحاث، أن يُدرك حقيقة الدين والعقيدة في مصر القديمة.",
        "وقام بإلقاء بعض المحاضرات عن حقيقة إيمان المصريين القدماء بالتوحيد في مصر القديمة، إضافةً إلى موضوعاتٍ أخرى، في بعض الولايات الأمريكية تلبيةً لدعوةِ بعض شركات السياحة الأمريكية التي عمل معها لسنوات. وآخر زياراته كانت لمدينة سيدني الأسترالية تلبيةً لدعوة رئيس برلمان ولاية نيو ساوث ويلز New South Wales لإلقاء محاضرتين في مقرّ البرلمان الأسترالي عام ١٩٩٩ عن آخر ما وصل إليه في دراساته حول أسرار الحضارة المصرية القديمة، وعن حقيقة اكتشاف المصريين القدماء لقارة أستراليا في عصر الملك سنفرو والملك خوفو.",
      ],
      bioBody: [
        "انتهى بحثُه إلى قراءةٍ مستقلّةٍ تُعيد الاعتبار لإيمان المصريين القدماء بالله الواحد، وتُثبت صلتهم بحضرات رسل الله على أرض مصر، وتُرجع إليهم ما ثبت لغيرهم من ريادةٍ في العلوم والآداب والأخلاق.",
        "وَرِثَ القرّاءَ كتابًا يُلخّص هذه الرحلة: «الحضارة المصرية القديمة — رؤية عقلية موثّقة».",
      ],
      timelineTitle: "محطّات",
      timeline: [
        {
          y: "٢٠١٥",
          t: "صدور الكتاب",
          b: "يصدر في ١ / ١ / ٢٠١٥ عن دار الفكر العربي.",
        },
        {
          y: "٢٠١٥ – ٢٠٢٥",
          t: "محاضرات وحوارات",
          b: "سلسلة من اللقاءات العامّة والمحاضرات والبرامج التلفزيونية تُشارك أفكار الكتاب، وقد امتدّت على مدى عشر سنوات.",
          items: [
            { y: "٢٠١٥", t: "بلاد طيبة مع هبة شامل (١٦ مارس ٢٠١٥)" },
            { y: "٢٠١٥", t: "محاضرتا رحلة النيل على قناة Nile TV (٧ مايو و١٢ نوفمبر ٢٠١٥)" },
            { y: "٢٠١٥", t: "ندوة بكلية الآثار — جامعة القاهرة" },
            { y: "٢٠١٥", t: "لقاء «في دائرة الضوء»" },
            { y: "٢٠١٥", t: "برنامج «مصر من تاني» مع إسلام العتيق" },
            { y: "٢٠١٥–٢٠١٦", t: "سلسلة «مصريات» — حقيقة التوحيد في مصر القديمة (٣ أجزاء)" },
            { y: "٢٠١٥–٢٠٢٥", t: "منشورات ومقالات مستمرّة على فيسبوك حول الحضارة المصرية القديمة" },
          ],
        },
        {
          y: "٢٠٢٦",
          t: "الطبعة الإنجليزيّة",
          b: "الترجمة الإنجليزية في مرحلتها النهائية من التحرير والمراجعة، وسيُكشف عن العنوان قريبًا.",
        },
      ],
      memNote:
        "رحم الله الأستاذ نبيل توفيق، وجعل ما كتبه ميراثًا فكريًا نافعًا، ونفع به كل قارئٍ صادق",
      portraitCap: "صورة المؤلّف — تُضاف لاحقًا",
    },
    legacyPage: {
      kicker: "الإرث",
      title: "أن تستمرّ الفكرة",
      body: [
        "حين يغيب الإنسان يبقى ما كتب، وما علّم، وما دعا إليه. الأستاذ نبيل توفيق ترك لنا كتابًا وفكرة: أن نقرأ تاريخنا بعقولنا، وأن نعتزّ بما كان عليه أجدادنا من إيمانٍ وعلمٍ وأخلاق.",
        "نحن — أهله ومحبّو فكره — نواصل هذه الرسالة بإتاحة كتابه لكل قارئ، وبجمع أفكاره ومحاضراته في هذا الموقع الذي نأمل أن يكون بيتًا رقميًا لمشروعه الفكري.",
      ],
      cta1: "اقتنِ الكتاب",
      cta2: "استكشف الأفكار",
    },
    contactPage: {
      kicker: "طلب الكتاب",
      title: "اطلب نسختك من الكتاب",
      intro:
        "اترك بياناتك وسيصلك تأكيد الطلب وتفاصيل الشحن في أقرب وقت. الشحن متاح داخل مصر وإلى عدد من الدول.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف (اختياري)",
      address: "العنوان بالكامل",
      governorate: "المحافظة",
      qty: "عدد النسخ",
      notes: "ملاحظات إضافيّة",
      send: "إرسال الطلب",
      sending: "جاري الإرسال…",
      submitError: "تعذّر إرسال الطلب، حاول مرة أخرى أو تواصل معنا عبر صفحة الكتاب على فيسبوك.",
      or: "أو",
      directTitle: "التواصل المباشر",
      direct:
        "يمكنكم التواصل عبر صفحة الكتاب على فيسبوك التي يُديرها ذوو المؤلف.",
      thanks: "شكرًا — تمّ استلام طلبك، وسنعاود التواصل.",
      facebookLabel: "صفحة الكتاب على فيسبوك",
    },
    meta: {
      home: {
        title: "نبيل توفيق · الموقع الرسمي",
        description:
          "الموقع الرسمي للأستاذ نبيل توفيق وكتابه «الحضارة المصرية القديمة — رؤية عقلية موثّقة».",
      },
      book: {
        title: "الكتاب · الحضارة المصرية القديمة — رؤية عقلية موثّقة",
        description:
          "نظرة تفصيلية على كتاب «الحضارة المصرية القديمة — رؤية عقلية موثّقة» للمؤلف نبيل توفيق.",
      },
      concepts: {
        title: "أفكار الكتاب · نبيل توفيق",
        description:
          "محاور وأفكار رئيسيّة من كتاب «الحضارة المصرية القديمة — رؤية عقلية موثّقة».",
      },
      videos: {
        title: "لقاءات وحوارات · نبيل توفيق",
        description: "أرشيف متنامٍ لمحاضرات وحوارات الأستاذ نبيل توفيق.",
      },
      posts: {
        title: "منشورات فيسبوك · نبيل توفيق",
        description: "أرشيف منشورات صفحة الأستاذ نبيل توفيق على فيسبوك.",
      },
      english: {
        title: "الطبعة الإنجليزية · Nabil Tawfik",
        description: "الترجمة الإنجليزية للكتاب قيد الإعداد.",
      },
      about: {
        title: "عن المؤلف · نبيل توفيق",
        description: "سيرة الأستاذ نبيل توفيق ومشروعه الفكري.",
      },
      legacy: {
        title: "الإرث · نبيل توفيق",
        description: "أن تستمرّ الفكرة — رسالة الأستاذ نبيل توفيق ومن تابعوا مشروعه.",
      },
      contact: {
        title: "اطلب الكتاب · نبيل توفيق",
        description: "اطلب نسختك من كتاب «الحضارة المصرية القديمة — رؤية عقلية موثّقة».",
      },
    },
  },

  en: {
    dir: "ltr" as const,
    langLabel: "ع",
    nav: {
      home: "Home",
      book: "The Book",
      concepts: "Ideas",
      videos: "Talks",
      posts: "Posts",
      english: "English Edition",
      about: "About",
      legacy: "Legacy",
      contact: "Order",
    },
    site: {
      name: "Nabil Tawfik",
      tagline: "Author · Scholar of Ancient Egyptian Civilization",
    },
    hero: {
      kicker: "A book by Nabil Tawfik",
      title1: "Ancient Egyptian Civilization",
      title2: "A Reasoned, Documented Reading",
      lede:
        "An invitation to re-read the oldest civilization humanity has known — grounded in text, monument and reason — restoring the evidence that ancient Egyptians believed in One God and followed the Messengers who walked this land.",
      ctaPrimary: "Order the Arabic Edition",
      ctaSecondary: "Explore the Ideas",
      frameCaption: "2015 edition · Available now",
    },
    featured: {
      kicker: "The Book",
      title: "A work that re-reads ancient Egyptian history",
      body1:
        "Since humanity began, the civilization of the Nile has stood as a witness to an age of scientific, architectural and spiritual wonder. Yet much that the orientalists transmitted arrived distorted — describing a civilization of unity as if it were a civilization of idols.",
      body2:
        "This book sets the record straight. Drawing on the ancient Egyptian texts themselves — papyri, temple and tomb walls — it shows that the greatest civilization in history was the fruit of faith in One God and obedience to His Messengers.",
      feature: [
        "Texts and papyri affirming belief in divine unity",
        "A fresh reading of ‘Pharaoh’ and the royal cartouche",
        "The roots of prayer, fasting, pilgrimage in ancient Egypt",
        "The ties between ancient Egyptian and Arabic",
      ],
      ctaBuy: "Order the Book",
      ctaExcerpt: "Read an Excerpt",
    },
    ideas: {
      kicker: "From the book",
      title: "Ideas that move the mind",
      intro:
        "A selection of the book’s central arguments. Each can be explored in full on the Ideas page — with cross-references to the original text.",
      seeAll: "See all ideas",
    },
    videos: {
      kicker: "Talks & Interviews",
      title: "The spoken word",
      intro:
        "Selected public lectures and interviews in which the author discusses the civilization of ancient Egypt and the faith of its people.",
      seeAll: "Watch more",
      placeholder: "Video forthcoming",
    },
    legacy: {
      kicker: "Legacy",
      title: "The mission continues",
      body:
        "After the passing of Prof. Nabil Tawfik — may God have mercy on him — those who loved him took up the trust: to keep his ideas accessible, and to ensure his book reaches every reader seeking a reasoned, documented reading of Egypt’s history. This site is the official home of that project.",
      cta: "Read about the legacy",
    },
    footer: {
      brand: "Official site of Prof. Nabil Tawfik",
      rights: "All rights reserved to Prof. Nabil Tawfik",
      ipNotice:
        "This website and all its texts and content are the exclusive property of the author. No part may be copied, republished, or distributed by any means without prior written permission.",
      nav1: "Explore",
      nav2: "Contact",
      email: "Email",
      social: "Follow",
      note: "Continually updated.",
    },
    bookPage: {
      headKicker: "Arabic Edition · First Printing",
      headTitle: "Ancient Egyptian Civilization — A Reasoned Reading",
      author: "By Nabil Tawfik",
      year: "Published 1 / 1 / 2015 · Dar Al-Fikr Al-Arabi",
      overviewTitle: "About the book",
      overview: [
        "A book that unveils many of the secrets of ancient Egyptian civilization and its connection to the Messengers of God who lived on the soil of Egypt. It draws on the original texts, papyri and inscriptions, and adopts a reasoned method that revisits the misconceptions established by earlier orientalists regarding ancient Egyptian ‘paganism’.",
        "The author presents layered evidence that ancient Egyptians believed in the Oneness of the Creator and performed acts of worship — prayer, fasting, pilgrimage, almsgiving — closely resembling those of Muslims today. He investigates the symbolic meanings of names and signs in the ancient Egyptian language and their deep connection to Arabic.",
      ],
      whyTitle: "Why this book matters",
      why: [
        { t: "A rigorous method", b: "Built on the original texts, not on second-hand translations." },
        { t: "Layered evidence", b: "Draws on papyri, temple walls, tomb reliefs and obelisks." },
        { t: "Lucid prose", b: "Addresses the general reader and the specialist alike." },
        { t: "Independent voice", b: "Free from the assumptions of the orientalist school." },
      ],
      sampleTitle: "From the introduction",
      sample:
        "“The ancient Egyptian civilization is distinguished by being the only immortal civilization whose traces have not vanished across thousands of years — telling the secrets of history since humanity first walked this earth.”",
      sampleSig: "— From the book’s introduction",
      orderTitle: "How to order",
      order: [
        { t: "Shipping within Egypt", b: "Available to all governorates. Delivery confirmed on order." },
        { t: "International shipping", b: "Available per country. Details shared with the buyer." },
        { t: "Payment", b: "Bank transfer · digital wallet · cash on delivery (Egypt)." },
      ],
      orderCta: "Order your copy",
      endorseTitle: "Readings & reviews",
      endorsePlaceholder: "A dedicated space for readers’ and thinkers’ responses — added as received.",
      flipToBack: "View back cover",
      flipToFront: "View front cover",
      librariesKicker: "In World Libraries",
      librariesTitle: "Held by major academic and national libraries",
      librariesIntro:
        "The book is part of the holdings of several leading university and national libraries worldwide, alongside availability through prominent Arabic online bookstores.",
      librariesViewCta: "View in catalog",
      retailersTitle: "Buy the book",
      retailersIntro:
        "The Arabic edition is available from the following online bookstores:",
    },
    englishPage: {
      kicker: "The English Edition",
      // Working English title isn't yet legally registered — placeholder
      // mirrors the cover image's "TITLE / to be revealed" panel.
      title: "Title to be revealed",
      subtitle: "Forthcoming — in final preparation",
      body1:
        "The English translation of Prof. Nabil Tawfik's Ancient Egyptian Civilization — A Reasoned, Documented Reading is in the final stages of editing and review. Leave your email to be the first to know when it ships.",
      body2:
        "We are committed to preserving the depth and precision of the original. The editorial process takes the time it needs.",
      formLabel: "Notify me when it’s published",
      formPlaceholder: "your@email",
      formCta: "Join the waitlist",
      formThanks: "Thank you — you’ll be notified at launch.",
    },
    conceptsPage: {
      kicker: "Ideas",
      title: "The book’s central themes",
      intro:
        "A catalogue of the core arguments and threads developed in the book. Expanded summaries with references to the original text will be added progressively.",
      noteLabel: "Editorial note:",
      note: "These titles are drawn directly from the author’s own chapter summary in the book’s introduction.",
      expandedTBA: "Expanded summary — forthcoming",
    },
    postsPage: {
      kicker: "Posts",
      title: "Posts from Facebook",
      intro:
        "A growing archive of Prof. Nabil Tawfik's Facebook posts. Bodies are in Arabic as originally published, with their attached photos. Source: the full Facebook page export, imported locally.",
      empty: "No posts available yet.",
      loadMore: "Load more",
      viewOriginal: "View original post",
      postKicker: "Facebook post",
      prev: "Previous",
      next: "Next",
      readMore: "Read post",
      photoCount: (n: number) => (n === 1 ? "1 photo" : `${n} photos`),
      photoAlt: (i: number, total: number, date: string) =>
        `Photo ${i} of ${total} — ${date}`,
      backToIndex: "Back to posts",
    },
    videosPage: {
      kicker: "Watch",
      title: "Talks & Interviews",
      intro:
        "A growing archive of Prof. Tawfik’s lectures and conversations. Watch the full playlist on YouTube; English transcripts are available for the two Nile Cruise lectures.",
      watchOnYoutube: "Open on YouTube",
      playlistLabel: "Official playlist",
      transcriptToggle: "Show transcript",
      transcriptHide: "Hide transcript",
      transcriptNA: "No transcript available for this video yet",
      arabicSummaryTBA: "Arabic summary — in preparation",
    },
    aboutPage: {
      kicker: "About the author",
      title: "Nabil Tawfik",
      role: "Author · Thinker · Scholar of Ancient Egyptian Civilization",
      bioLead:
        "Prof. Nabil Tawfik devoted decades of his life to the study of ancient Egyptian civilization — its temples, papyri and inscriptions — pursuing a single, essential question: what was the true faith on which this civilization was built?",
      // Authoritative biographical paragraphs translated from the back
      // cover of the Arabic first edition. Rendered as a distinct,
      // visually-tagged section on the About page so the reader can see
      // clearly which paragraphs are by the author and which are
      // editorial.
      bioFromBackCoverHeading: "About the Author — from the book's back cover",
      bioFromBackCover: [
        "Nabil Tawfik graduated from the Faculty of Tourism and Hotels, Helwan University, in 1976, in the Tourism Guidance department, and studied the ancient Egyptian language. He also studied the history and antiquities of ancient Egypt across its different eras. Through thirty-six years of fieldwork in tourism guidance — with on-site visits to archaeological sites throughout Egypt — together with extensive reading and research, he came to grasp the true nature of religion and faith in ancient Egypt.",
        "He delivered lectures on the reality of the ancient Egyptians' belief in monotheism, alongside other topics, in several American states at the invitation of American tourism companies he had worked with for years. His final journey was to Sydney, Australia, at the invitation of the President of the New South Wales Parliament — where he gave two lectures in 1999 at the Australian Parliament building on his most recent findings about the secrets of ancient Egyptian civilization, and on the truth that ancient Egyptians had discovered the continent of Australia during the reigns of King Sneferu and King Khufu.",
      ],
      bioBody: [
        "His work ended in an independent reading that restores the evidence of the ancient Egyptians’ belief in One God, establishes their connection to the Messengers of God in Egypt, and returns to them what had been credited to others in the sciences, letters and ethics.",
        "He left readers a book that distills this journey: Ancient Egyptian Civilization — A Reasoned, Documented Reading.",
      ],
      timelineTitle: "Highlights",
      timeline: [
        {
          y: "2015",
          t: "Publication",
          b: "Released 1 January 2015 by Dar Al-Fikr Al-Arabi.",
        },
        {
          y: "2015 – 2025",
          t: "Public talks & writings",
          b: "A decade of public lectures, interviews and TV appearances sharing the book's ideas.",
          items: [
            { y: "2015", t: "Belad Teeba with Heba Shamel (16 Mar 2015)" },
            { y: "2015", t: "Nile Cruise lectures on Nile TV (7 May & 12 Nov 2015)" },
            { y: "2015", t: "Seminar at the Faculty of Archaeology, Cairo University" },
            { y: "2015", t: "Fi Daerat El-Daw interview" },
            { y: "2015", t: "Misr Men Tany with Islam El-Ateeq" },
            { y: "2015–2016", t: "Masriyat — The Truth of Monotheism in Ancient Egypt (3-part series)" },
            { y: "2015–2025", t: "Ongoing Facebook writings on ancient Egyptian civilization" },
          ],
        },
        {
          y: "2026",
          t: "English edition",
          b: "The English edition is in the final stages of editing and review; the title will be revealed soon.",
        },
      ],
      memNote:
        "May God have mercy on Prof. Nabil Tawfik, place his work in his balance of good deeds, and benefit every sincere reader through it.",
      portraitCap: "Author portrait — to be added",
    },
    legacyPage: {
      kicker: "Legacy",
      title: "That the idea may endure",
      body: [
        "When a person departs, what remains is what they wrote, what they taught and what they called toward. Prof. Nabil Tawfik left us a book and a call: to read our own history with our own minds, and to honour what our ancestors were — a people of faith, knowledge and ethics.",
        "We — his family and those who love his work — continue this mission by making his book available to every reader, and by gathering his ideas and talks on this site, which we hope will be the digital home of his project.",
      ],
      cta1: "Order the book",
      cta2: "Explore the ideas",
    },
    contactPage: {
      kicker: "Order",
      title: "Order your copy of the book",
      intro:
        "Leave your details and we will confirm your order and shipping details shortly. We ship within Egypt and to several countries abroad.",
      name: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      address: "Full address",
      governorate: "Governorate",
      qty: "Copies",
      notes: "Additional notes",
      send: "Submit order",
      sending: "Sending…",
      submitError: "Could not submit the order — please try again or reach us via the book's Facebook page.",
      or: "or",
      directTitle: "Direct contact",
      direct:
        "You may also reach us via the book's Facebook page, maintained by the author's family.",
      thanks: "Thank you — your order has been received. We'll be in touch.",
      facebookLabel: "Book page on Facebook",
    },
    meta: {
      home: {
        title: "Nabil Tawfik · Official Site",
        description:
          "The official site of Prof. Nabil Tawfik and his book Ancient Egyptian Civilization: A Reasoned, Documented Reading.",
      },
      book: {
        title: "The Book · Ancient Egyptian Civilization",
        description:
          "A detailed look at Ancient Egyptian Civilization — A Reasoned, Documented Reading by Nabil Tawfik.",
      },
      concepts: {
        title: "Ideas · Nabil Tawfik",
        description: "Central themes and arguments from the book.",
      },
      videos: {
        title: "Talks & Interviews · Nabil Tawfik",
        description: "A growing archive of lectures and conversations.",
      },
      posts: {
        title: "Facebook posts · Nabil Tawfik",
        description: "Archive of Prof. Nabil Tawfik's Facebook page posts.",
      },
      english: {
        title: "English Edition · Nabil Tawfik",
        description: "The English translation of the book is forthcoming.",
      },
      about: {
        title: "About the Author · Nabil Tawfik",
        description: "Biography and intellectual project of Prof. Nabil Tawfik.",
      },
      legacy: {
        title: "Legacy · Nabil Tawfik",
        description: "That the idea may endure — the author's message and those who carry it forward.",
      },
      contact: {
        title: "Order the Book · Nabil Tawfik",
        description: "Order your copy of Ancient Egyptian Civilization — A Reasoned, Documented Reading.",
      },
    },
  },
} as const;
